import { Box, Button, TextField, Typography, Alert, ToggleButton, ToggleButtonGroup, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import useHandleApiResponse from '../../../../../Hooks/useHandleApiResponse';
import { getLensSyncData, tutorSync } from '../../../../../redux/features/lensSyncSlice';
import { useSelector } from 'react-redux';

export default function TutorSyncModal({ row, onClose }) {
  const dispatch = useDispatch();
  const handleApiResponse = useHandleApiResponse();

  const { loading } = useSelector((state) => state.lens);

  const [type, setType] = useState('email');
  const [confirmed, setConfirmed] = useState(false);

  const isAlreadySynced = Boolean(row?.wise_teacher_id);
  const tutorName = row?.full_name || 'N/A';

  const handleConfirm = () => {
    setConfirmed(true);
  };

  // ✅ format country code
  const formatCountryCode = (code) => {
    if (!code) return '+91';

    const stringCode = String(code).trim();

    if (stringCode.startsWith('+')) return stringCode;

    if (/^[0-9]+$/.test(stringCode)) return `+${stringCode}`;

    return '+91'; // fallback safety
  };

  // ✅ Initial Values
  const initialValues = {
    email: row?.email_id || '',
    phone: row?.mob_no || '',
    countryCode: formatCountryCode(row?.a_phone_country_code),
    subjectEnrollmentId: row?.subject_enrollment_id,
    tutorId: row?.trainer_id,
    alreadyMapped: row?.wise_teacher_id ? true : false,
  };

  // ✅ Dynamic Validation
  const validationSchema = Yup.object({
    email: Yup.string().when('type', {
      is: 'email',
      then: (schema) => schema.email('Enter valid email address').required('Email is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    phone: Yup.string().when('type', {
      is: 'phone',
      then: (schema) =>
        schema.matches(/^[0-9]{6,14}$/, 'Enter valid phone number').required('Phone number is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    countryCode: Yup.string().when('type', {
      is: 'phone',
      then: (schema) => schema.required('Country code required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  // ✅ Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      // Only send the identifier the user actually selected so the backend
      // doesn't fall back to email when phone was chosen (and vice versa).
      const payload = {
        subjectEnrollmentId: values.subjectEnrollmentId,
        tutorId: values.tutorId,
        alreadyMapped: values.alreadyMapped,
      };

      if (type === 'phone') {
        payload.mobile = values.phone;
        payload.countryCode = values.countryCode;
      } else {
        payload.email = values.email;
      }

      dispatch(tutorSync(payload)).then((res) => {
        handleApiResponse(res, {
          onSuccess: () => {
            onClose();
          },
          onFailed: () => {
            console.log('Course sync Failed');
          },
        });
      });
    },
  });

  if (!row) return null;

  return (
    <Box p={3}>
      <Typography fontWeight={600} mb={1}>
        Tutor: {tutorName}
      </Typography>

      {!confirmed ? (
        <>
          {isAlreadySynced ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Notice</strong>
              <br />
              Already synced. Re-sync will refresh data.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Disclaimer</strong>
              <br />
              Tutor will be synced with Lens/Wise.
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirm}>
              Continue
            </Button>
          </Box>
        </>
      ) : (
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              {/* TOGGLE */}
              <ToggleButtonGroup
                value={type}
                exclusive
                fullWidth
                onChange={(e, newValue) => {
                  if (newValue) {
                    setType(newValue);
                    formik.setFieldValue('type', newValue);
                    formik.resetForm();
                  }
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="email">Email</ToggleButton>
                <ToggleButton value="phone">Phone</ToggleButton>
              </ToggleButtonGroup>

              {/* EMAIL */}
              {type === 'email' && (
                <TextField
                  name="email"
                  label="Tutor Lens Email"
                  fullWidth
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              )}

              {/* PHONE */}
              {type === 'phone' && (
                <Box display="flex" gap={2}>
                  <TextField
                    name="countryCode"
                    label="Country Code"
                    value={formik.values.countryCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.countryCode && Boolean(formik.errors.countryCode)}
                    helperText={formik.touched.countryCode && formik.errors.countryCode}
                    sx={{ width: '35%' }}
                  />

                  <TextField
                    name="phone"
                    label="Mobile Number"
                    fullWidth
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                  />
                </Box>
              )}

              {/* ACTIONS */}
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button onClick={() => setConfirmed(false)}>Back</Button>
                <Button variant="contained" type="submit" disabled={!formik.isValid}>
                  Sync
                </Button>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      )}
    </Box>
  );
}
